//ADDITIONAL_DEPENDENCIES:wc-blocks-data-store
import {select, subscribe} from "@wordpress/data";

import {type GaItem, round} from "./view-item-common";

/**
 * The interaction events for the block-based cart & checkout: add_to_cart, remove_from_cart,
 * add_shipping_info and add_payment_info.
 *
 * These all happen over the Store API with no page reload, so they can't be server-rendered like the page-load
 * events. Rather than the `experimental__woocommerce_blocks-*` action hooks (an unstable, prefixed interface), we
 * subscribe to WooCommerce's public @wordpress/data stores and react to state changes:
 *   - wc/store/cart     -> line-item quantity diffs (add/remove) and the selected shipping rate
 *   - wc/store/payment  -> the active payment method
 *   - wc/store/checkout -> the submit lifecycle, used to flush shipping/payment when the shopper accepts defaults
 *
 * The GA4 item metadata (item_id, item_brand, item_category*, price) is computed server-side and exposed on each
 * Store API cart item under `extensions.plaudit_ga4_ecommerce.item` (see the PHP entrypoint), keeping a single
 * source of truth with view_item / view_item_list. We fall back to the native Store API fields if it is absent.
 */

const STORE_API_EXTENSION = "plaudit_ga4_ecommerce";

/**
 * This method is a hack to make grabbing store descriptors a bit easier.
 * Its return type is guaranteed to be incorrect, but "unknown" won't compile and I don't care enough to derive the actual types.
 */
function getWCStoreDescriptor(name: 'cart'|'payment'|'checkout'): string {
	return (window as any).wc?.wcBlocksData?.[`${name}Store`];
}

/** Store API prices are integers in the currency's minor unit (e.g. cents); convert back to a major-unit number. */
function minorUnitToNumber(value: any, minorUnit: any): number | undefined {
	const amount = parseInt(value, 10);
	if (!Number.isFinite(amount)) {
		return undefined;
	}
	const precision = Number.isFinite(parseInt(minorUnit, 10)) ? parseInt(minorUnit, 10) : 2;
	return amount / 10 ** precision;
}

/** Builds a GA4 item from a Store API cart item, preferring the server-computed object over native fields. */
function buildGaItem(cartItem: any): GaItem | null {
	if (!cartItem || typeof cartItem !== 'object') {
		return null;
	}

	const serverItem = cartItem.extensions?.[STORE_API_EXTENSION]?.item;
	if (typeof serverItem === 'string' && serverItem) {
		try {
			const item = JSON.parse(serverItem);
			if (item && typeof item === 'object' && 'item_id' in item && 'item_name' in item) {
				return item;
			}
		} catch {
			// fall through to the native reconstruction below
		}
	}

	if (!cartItem.name) {
		return null;
	}

	const item: GaItem = {
		item_id: cartItem.sku || String(cartItem.id ?? ""),
		item_name: cartItem.name,
	};
	if (Array.isArray(cartItem.categories)) {
		cartItem.categories.slice(0, 5).forEach((category: any, index: number) => {
			if (category?.name) {
				item[index > 0 ? `item_category${index + 1}` : "item_category"] = category.name;
			}
		});
	}
	const price = minorUnitToNumber(cartItem.prices?.price, cartItem.prices?.currency_minor_unit);
	if (price !== undefined) {
		item.price = price;
	}

	return item.item_id && item.item_name ? item : null;
}

// -------------------------------------------------------------------------------------------------
// add_to_cart / remove_from_cart — driven by diffing cart line quantities across store updates.
// -------------------------------------------------------------------------------------------------

interface CartLine {
	quantity: number;
	item: any;
}

let cartBaseline: Map<string, CartLine> | null = null;
let cartReady = false;

function snapshotCart(cartData: any): Map<string, CartLine> {
	const lines = new Map<string, CartLine>();
	for (const cartItem of cartData?.items ?? []) {
		if (cartItem?.key != null) {
			lines.set(String(cartItem.key), {quantity: Number(cartItem.quantity) || 0, item: cartItem});
		}
	}
	return lines;
}

function emitItemEvent(eventName: string, cartItem: any, quantity: number): void {
	if (!window.gtag) {
		return;
	}

	const gaItem = buildGaItem(cartItem);
	if (!gaItem) {
		return;
	}

	const resolvedQuantity = quantity > 0 ? quantity : 1;
	const event: { currency?: string, value?: number, items: GaItem[] } = {
		items: [{...gaItem, quantity: resolvedQuantity}],
	};

	const currency = cartItem?.prices?.currency_code;
	if (currency) {
		event.currency = currency;
	}
	if (typeof gaItem.price === 'number') {
		event.value = round(gaItem.price * resolvedQuantity);
	}

	window.gtag("event", eventName, event);
}

/**
 * Compares the latest cart against the previous snapshot and fires an event for each changed line. A quantity
 * increase (from any source — product button, single-product page, mini-cart, in-cart stepper) is an add_to_cart;
 * a decrease or removal is a remove_from_cart. The very first observed cart becomes the baseline and fires nothing.
 */
function reconcileCart(cartData: any): void {
	const next = snapshotCart(cartData);

	// Until the cart store has finished hydrating its preloaded/fetched data, keep the latest snapshot as a
	// tentative baseline without firing — otherwise the initial empty -> populated transition on a page load
	// would look like the shopper adding every existing line item.
	if (!cartReady) {
		cartBaseline = next;
		if (select(getWCStoreDescriptor('cart'))?.hasFinishedResolution?.('getCartData')) {
			cartReady = true;
		}
		return;
	}

	const baseline = cartBaseline;
	if (!baseline) {
		cartBaseline = next;
		return;
	}

	for (const [key, line] of next) {
		const previousQuantity = baseline.get(key)?.quantity ?? 0;
		if (line.quantity > previousQuantity) {
			emitItemEvent("add_to_cart", line.item, line.quantity - previousQuantity);
		}
	}
	for (const [key, line] of baseline) {
		const currentQuantity = next.get(key)?.quantity ?? 0;
		if (currentQuantity < line.quantity) {
			emitItemEvent("remove_from_cart", next.get(key)?.item ?? line.item, line.quantity - currentQuantity);
		}
	}

	cartBaseline = next;
}

// -------------------------------------------------------------------------------------------------
// add_shipping_info / add_payment_info — driven by shipping-rate and payment-method selection, with a
// checkout-submit fallback so accepting the pre-selected defaults still produces an event.
// -------------------------------------------------------------------------------------------------

let shippingBaselineCaptured = false;
let lastShippingRateId: string | null = null;
let shippingFired = false;

let paymentBaselineCaptured = false;
let lastPaymentMethod: string | null = null;
let paymentFired = false;

let checkoutFlushed = false;

/** Builds the whole-cart payload ({currency, value, items}) shared by the shipping and payment events. */
function buildCartWidePayload(cartData: any): { currency?: string, value: number, items: GaItem[] } | null {
	const items: GaItem[] = [];
	let value = 0;
	for (const cartItem of cartData?.items ?? []) {
		const gaItem = buildGaItem(cartItem);
		if (!gaItem) {
			continue;
		}
		const quantity = Number(cartItem.quantity) || 1;
		items.push({...gaItem, quantity});
		if (typeof gaItem.price === 'number') {
			value += gaItem.price * quantity;
		}
	}

	if (!items.length) {
		return null;
	}

	const payload: { currency?: string, value: number, items: GaItem[] } = {value: round(value), items};
	const currency = cartData?.totals?.currency_code ?? cartData?.items?.[0]?.prices?.currency_code;
	if (currency) {
		payload.currency = currency;
	}
	return payload;
}

function selectedShippingRate(cartData: any): { id?: string, name?: string } {
	for (const shippingPackage of cartData?.shippingRates ?? []) {
		for (const rate of shippingPackage?.shipping_rates ?? []) {
			if (rate?.selected) {
				return {id: rate.rate_id, name: rate.name};
			}
		}
	}
	return {};
}

/** Resolves a payment method slug to its human-readable title via the block payment settings, if available. */
function paymentTitle(slug: string | null): string | undefined {
	if (!slug) {
		return undefined;
	}
	const paymentMethodData = (window as any).wc?.wcSettings?.getSetting?.("paymentMethodData");
	return paymentMethodData?.[slug]?.title || slug;
}

function fireShippingInfo(cartData: any, shippingTier?: string): void {
	if (!window.gtag) {
		return;
	}
	const payload = buildCartWidePayload(cartData);
	if (!payload) {
		return;
	}
	window.gtag("event", "add_shipping_info", shippingTier ? {...payload, shipping_tier: shippingTier} : payload);
	shippingFired = true;
}

function firePaymentInfo(cartData: any, paymentType?: string): void {
	if (!window.gtag) {
		return;
	}
	const payload = buildCartWidePayload(cartData);
	if (!payload) {
		return;
	}
	window.gtag("event", "add_payment_info", paymentType ? {...payload, payment_type: paymentType} : payload);
	paymentFired = true;
}

function reconcileCheckout(cartData: any): void {
	// Shipping: fire whenever the shopper selects a different rate. The first rate we observe as selected (the
	// hydrated default) becomes the baseline and is not treated as a change.
	if (cartData) {
		const {id, name} = selectedShippingRate(cartData);
		if (!shippingBaselineCaptured) {
			if (id) {
				lastShippingRateId = id;
				shippingBaselineCaptured = true;
			}
		} else if (id && id !== lastShippingRateId) {
			lastShippingRateId = id;
			fireShippingInfo(cartData, name);
		}
	}

	// Payment: fire whenever the active payment method changes. The first non-null method we observe (the
	// hydrated default) becomes the baseline and is not treated as a change.
	const activeMethod = select(getWCStoreDescriptor('payment'))?.getActivePaymentMethod?.() || null;
	if (!paymentBaselineCaptured) {
		if (activeMethod) {
			lastPaymentMethod = activeMethod;
			paymentBaselineCaptured = true;
		}
	} else if (activeMethod && activeMethod !== lastPaymentMethod) {
		lastPaymentMethod = activeMethod;
		firePaymentInfo(cartData, paymentTitle(activeMethod));
	}

	const checkout = select(getWCStoreDescriptor('checkout'));

	// Submit fallback: when the order is placed, emit whichever event the shopper never triggered by changing a
	// selection (i.e. they accepted the pre-selected default). Guarded so it only flushes once per submission.
	if (checkout?.isBeforeProcessing?.() || checkout?.isProcessing?.()) {
		if (!checkoutFlushed) {
			checkoutFlushed = true;
			if (!shippingFired && cartData) {
				fireShippingInfo(cartData, selectedShippingRate(cartData).name);
			}
			if (!paymentFired) {
				firePaymentInfo(cartData, paymentTitle(lastPaymentMethod));
			}
		}
	} else if (checkout?.isIdle?.()) {
		checkoutFlushed = false;
	}
}

// -------------------------------------------------------------------------------------------------
// view_cart (mini-cart) — the mini-cart drawer opens client-side with no page load, so the server-rendered
// view_cart never covers it. The block signals its open state purely through CSS classes on the drawer's
// screen overlay (the "--is-hidden" modifier is present while closed), so we watch that element for a
// closed -> open transition and emit view_cart from the live cart data. The main /cart/ page keeps its own
// server-rendered view_cart. (No experimental/internal APIs — just the drawer's public DOM state.)
// -------------------------------------------------------------------------------------------------

const MINI_CART_DRAWER_CLASS = "wc-block-mini-cart__drawer";

function observeMiniCart(): void {
	const drawers = document.querySelectorAll(`.${MINI_CART_DRAWER_CLASS}`);
	if (drawers.length) {
		// The drawer is closed at load, so this baseline never fires — only a later real open does.
		const observer = new MutationObserver(mutationList => {
			for (const mutation of mutationList) {
				if (window.gtag && mutation.type === "attributes" && mutation.oldValue === 'true' && (mutation.target instanceof HTMLElement) && mutation.target.ariaHidden === 'false') {
					const payload = buildCartWidePayload(select(getWCStoreDescriptor('cart'))?.getCartData?.());
					if (payload) {
						window.gtag("event", "view_cart", payload);
					}
				}
			}
		});
		for (const drawer of drawers) {
			observer.observe(drawer, {
				attributeFilter: ['aria-hidden'],
				attributeOldValue: true
			});
		}
		return;
	}

	// The drawer has not been rendered yet — wait for it to appear, then attach the scoped observer above.
	const bodyObserver = new MutationObserver(() => {
		if (document.querySelector(`.${MINI_CART_DRAWER_CLASS}`)) {
			bodyObserver.disconnect();
			observeMiniCart();
		}
	});
	bodyObserver.observe(document.body, {subtree: true, childList: true});
}

// -------------------------------------------------------------------------------------------------

function onStoreChange(): void {
	const cartData = select(getWCStoreDescriptor('cart'))?.getCartData?.();
	if (cartData) {
		reconcileCart(cartData);
	}
	reconcileCheckout(cartData);
}

subscribe(onStoreChange);
// Capture the initial baselines immediately in case the stores are already hydrated when this runs.
onStoreChange();

observeMiniCart();
